import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles } from "lucide-react";
import API from "../Api";
import AiMarkdown from "./AiMarkdown";
import "./AskCommrayAi.css";

const KNOWN_ERRORS = [
  "INVALID_REQUEST",
  "AI_UPSTREAM_ERROR",
  "AI_NOT_CONFIGURED",
  "AI_INVALID_RESPONSE",
  "AI_VALIDATION_FAILED",
  "UNSUPPORTED_ACTION",
  "DRAFT_ONLY",
  "FORBIDDEN"
];

function messageForFailure(error) {
  const status = error?.response?.status;
  const body = error?.response?.data;
  if (body?.type === "ACTION_ERROR") {
    return body.message || "AI returned an invalid structured response";
  }
  const known = body && typeof body === "object" && typeof body.message === "string"
    && KNOWN_ERRORS.includes(body.error);
  if (known) return body.message;
  if (!error?.response) {
    return "Commray AI could not be reached. Check your connection and try again.";
  }
  if (status === 401) return "Your session has expired. Sign in again and try again.";
  if (status === 400) return "Enter a question and try again.";
  if (status >= 500) return "Commray AI is unavailable right now. Try again in a moment.";
  return "Commray AI could not answer that question. Try again.";
}

function actionLabel(action, operation) {
  if (action === "GENERATE_LOG_QUERY") return "Generate log query";
  if (action === "GENERATE_ALERT") return "Generate alert";
  if (action === "GENERATE_DASHBOARD") return "Generate dashboard";
  if (action === "GENERATE_WIDGET") return operation === "UPDATE" ? "Edit widget" : "Create widget";
  return action || "Draft";
}

function widgetValue(draft, field, emptyLabel) {
  if (!Object.prototype.hasOwnProperty.call(draft, field)) return "";
  const value = draft[field];
  return value ? String(value) : emptyLabel;
}

function formatDay(value) {
  const date = String(value || "").split("T")[0];
  const [year, month, day] = date.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (!year || !month || !day) return value || "";
  return `${months[month - 1]} ${day}, ${year}`;
}

function inclusiveEnd(value) {
  const stamp = String(value || "");
  const [date] = stamp.split("T");
  if (!stamp.endsWith("T00:00:00")) return formatDay(stamp);
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return formatDay(stamp);
  const previous = new Date(Date.UTC(year, month - 1, day));
  previous.setUTCDate(previous.getUTCDate() - 1);
  const iso = [
    previous.getUTCFullYear(),
    String(previous.getUTCMonth() + 1).padStart(2, "0"),
    String(previous.getUTCDate()).padStart(2, "0")
  ].join("-");
  return formatDay(iso);
}

function timeRangeLabel(range) {
  if (!range) return "";
  if (range.type === "RELATIVE") return range.value || "";
  if (range.type !== "ABSOLUTE") return "";
  const end = range.endExclusive ? inclusiveEnd(range.end) : formatDay(range.end);
  return `${formatDay(range.start)} → ${end}`;
}

function fieldSummary(draft) {
  const items = Array.isArray(draft.fields) && draft.fields.length > 0
    ? draft.fields
    : draft.field
      ? [{ field: draft.field, value: draft.value, match: "equals" }]
      : [];
  return items.map((item) => {
    if (!item.value) return item.field;
    const operator = item.match === "contains" ? "contains" : "=";
    return `${item.field} ${operator} ${item.value}`;
  }).join(" AND ");
}

function confirmLabel(action, operation, saving) {
  if (saving) return "Saving…";
  if (action === "GENERATE_WIDGET") return operation === "UPDATE" ? "Save changes" : "Create widget";
  if (action === "GENERATE_DASHBOARD") return operation === "UPDATE" ? "Save changes" : "Create dashboard";
  return "Save";
}

function DraftView({
  result,
  onUseInExplorer,
  onConfirm,
  saving,
  saveError,
  saved,
  dashboards,
  dashboardsLoaded,
  dashboardChoice,
  onDashboardChoice,
  acknowledged,
  onAcknowledge
}) {
  const draft = result.draft || {};
  const [notice, setNotice] = useState("");
  const rule = draft.ruleConfig || {};
  const canApply = result.applySupported === true;
  const needsDashboard = canApply && result.action === "GENERATE_WIDGET" && result.operation !== "UPDATE" && !draft.dashboardId;
  const needsAcknowledgement = draft.requiresAcknowledgement === true;
  const blocked = draft.valid === false;
  const confirmDisabled = saving || Boolean(saved) || blocked
    || (needsDashboard && !dashboardChoice)
    || (needsAcknowledgement && !acknowledged);
  return (
    <section className="ask-ai-draft" aria-label="AI generated draft">
      <p className="ask-ai-draft-banner">AI GENERATED DRAFT</p>
      <p className="ask-ai-draft-kicker">AI Action</p>
      <h3>{actionLabel(result.action, result.operation)}</h3>
      {result.explanation && <p>{result.explanation}</p>}
      {result.action === "GENERATE_LOG_QUERY" && (
        <>
          {draft.index && <p><strong>Index</strong><br />{draft.index}</p>}
          {draft.application && <p><strong>Application</strong><br />{draft.application}</p>}
          {(draft.index || draft.application) && (
            <p>Index and application are Log Explorer source filters. They are not part of the LogQL text.</p>
          )}
          {fieldSummary(draft) && (
            <p><strong>Fields</strong><br />{fieldSummary(draft)}</p>
          )}
          {draft.contentFormat && <p><strong>Format</strong><br />{draft.contentFormat}</p>}
          {Array.isArray(draft.httpStatuses) && draft.httpStatuses.length > 0 && (
            <p><strong>HTTP Status Codes</strong><br />{draft.httpStatuses.join(" OR ")}</p>
          )}
          <p><strong>Time Range</strong><br />{timeRangeLabel(draft.timeRange)}</p>
          <p><strong>Generated Commray Query</strong></p>
          <pre className="ask-ai-code">{draft.query}</pre>
          {draft.validation && <p><strong>Validation</strong><br />{draft.validation}</p>}
          {draft.warning && <p className="ask-ai-warning">{draft.warning}</p>}
          {draft.description && <p>{draft.description}</p>}
        </>
      )}
      {result.action === "GENERATE_ALERT" && (
        <>
          <p><strong>Alert name</strong><br />{draft.name}</p>
          <p><strong>Condition</strong><br />{draft.conditionSummary || rule.type}</p>
          <p><strong>Window</strong><br />{rule.windowMinutes} minutes</p>
          <p><strong>Category</strong><br />{draft.category}</p>
          <p><strong>Query</strong></p>
          <pre className="ask-ai-code">{draft.query}</pre>
        </>
      )}
      {result.action === "GENERATE_WIDGET" && (
        <>
          {draft.title && <p><strong>Title</strong><br />{draft.title}</p>}
          {draft.type && <p><strong>Type</strong><br />{draft.type}</p>}
          {draft.dataSource && <p><strong>Data source</strong><br />{draft.dataSource}</p>}
          {widgetValue(draft, "service", "All services") && (
            <p><strong>Service</strong><br />{widgetValue(draft, "service", "All services")}</p>
          )}
          {widgetValue(draft, "logLevel", "All levels") && (
            <p><strong>Log level</strong><br />{widgetValue(draft, "logLevel", "All levels")}</p>
          )}
          {draft.theme && <p><strong>Background</strong><br />{draft.theme}</p>}
          {draft.groupBy && (
            <p><strong>Series</strong><br />{draft.groupBy === "none" ? "Single series (total count)" : draft.groupBy}</p>
          )}
          {draft.chartColor && <p><strong>Chart color</strong><br />{draft.chartColor}</p>}
          {Object.prototype.hasOwnProperty.call(draft, "customEndpoint") && (
            <p><strong>Custom endpoint</strong><br />{draft.customEndpoint}</p>
          )}
          {draft.endpointLimitation && <p className="ask-ai-warning">{draft.endpointLimitation}</p>}
          {result.operation === "UPDATE" && (
            <p>This updates widget {draft.widgetId} on dashboard {draft.dashboardId}. A new widget will not be created.</p>
          )}
          {Array.isArray(draft.validationIssues) && draft.validationIssues.some((issue) => issue !== draft.endpointLimitation) && (
            <p className="ask-ai-warning">
              {draft.validationIssues.filter((issue) => issue !== draft.endpointLimitation).join(" ")}
            </p>
          )}
        </>
      )}
      {result.action === "GENERATE_DASHBOARD" && (
        <>
          <p><strong>Dashboard</strong><br />{draft.name}</p>
          <p>{draft.description}</p>
          <ul className="ask-ai-panels">
            {(draft.panels || []).map((panel) => (
              <li key={`${panel.title}-${panel.type}`}>
                <strong>{panel.title}</strong>
                <span>{panel.type}</span>
                {panel.logql && <pre className="ask-ai-code">{panel.logql}</pre>}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="ask-ai-draft-note">
        {saved
          ? `Saved ${saved.dashboardName}.`
          : canApply
            ? "Nothing has been saved yet. Review this draft, then confirm to save it."
            : "Nothing has been saved."}
      </p>
      {needsDashboard && dashboardsLoaded && (dashboards || []).length === 0 && (
        <p className="ask-ai-warning">Create a dashboard before adding this widget.</p>
      )}
      {needsDashboard && (
        <label className="ask-ai-label">
          Dashboard
          <select
            aria-label="Dashboard"
            value={dashboardChoice}
            onChange={(event) => onDashboardChoice(event.target.value)}
            disabled={saving || Boolean(saved)}
          >
            <option value="">Choose a dashboard</option>
            {(dashboards || []).map((dashboard) => (
              <option key={dashboard.id} value={dashboard.id}>{dashboard.name}</option>
            ))}
          </select>
        </label>
      )}
      {needsAcknowledgement && (
        <label className="ask-ai-check">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => onAcknowledge(event.target.checked)}
            disabled={saving || Boolean(saved)}
          />
          Create the logs widget without this custom endpoint. The URL will not be called or saved.
        </label>
      )}
      {result.action === "GENERATE_DASHBOARD" && result.operation === "UPDATE" && (
        <p>This updates dashboard {draft.dashboardId}. Existing widgets stay, and the draft panels are added.</p>
      )}
      {notice && <p className="ask-ai-warning" role="status">{notice}</p>}
      {saveError && <p className="ask-ai-error" role="alert">{saveError}</p>}
      <div className="ask-ai-draft-actions">
        <button type="button" className="ask-ai-secondary" onClick={result.onCancel} disabled={saving}>Cancel</button>
        <button type="button" className="ask-ai-secondary" onClick={result.onEdit} disabled={saving}>Edit</button>
        {result.action === "GENERATE_LOG_QUERY" && onUseInExplorer && (
          <button
            type="button"
            className="ask-ai-secondary"
            onClick={() => {
              const problem = onUseInExplorer(draft);
              setNotice(problem || "Copied to Log Explorer. Search has not been run and nothing was saved.");
            }}
          >
            Use in Log Explorer
          </button>
        )}
        {canApply ? (
          <button type="button" className="ask-ai-submit" onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel(result.action, result.operation, saving)}
          </button>
        ) : (
          <button type="button" className="ask-ai-submit" disabled title="This draft is not saved from here.">
            Apply
          </button>
        )}
        {saved?.dashboardName && (
          <button
            type="button"
            className="ask-ai-secondary"
            onClick={() => {
              window.location.assign(`/dashboards/${encodeURIComponent(saved.dashboardName)}/dashboard-viewer`);
            }}
          >
            Open dashboard
          </button>
        )}
      </div>
    </section>
  );
}

export default function AskCommrayAi({ onUseInExplorer }) {
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [dashboardsLoaded, setDashboardsLoaded] = useState(false);
  const [dashboardChoice, setDashboardChoice] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !loading && !saving) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, saving]);

  const close = () => {
    if (!loading && !saving) setOpen(false);
  };

  const hasContent = Boolean(question.trim() || answer || draft || error);

  useEffect(() => {
    const needs = draft?.applySupported === true
      && draft.action === "GENERATE_WIDGET"
      && draft.operation !== "UPDATE"
      && !draft.draft?.dashboardId;
    if (!needs) return undefined;
    let cancelled = false;
    setDashboardsLoaded(false);
    API.get("/api/dashboard")
      .then((response) => {
        if (!cancelled) setDashboards(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setSaveError("Dashboards could not be loaded. Nothing was saved.");
      })
      .finally(() => {
        if (!cancelled) setDashboardsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [draft]);

  const clear = () => {
    if (loading || saving) return;
    setQuestion("");
    setAnswer("");
    setDraft(null);
    setError("");
    setSaveError("");
    setSaved(null);
    setDashboards([]);
    setDashboardsLoaded(false);
    setDashboardChoice("");
    setAcknowledged(false);
  };

  const confirmDraft = async () => {
    if (!draft || saving || saved) return;
    const dashboardId = draft.draft?.dashboardId || Number(dashboardChoice);
    setSaving(true);
    setSaveError("");
    try {
      const response = await API.post("/api/ai/action/apply", {
        action: draft.action,
        operation: draft.operation || "CREATE",
        requestId: draft.requestId,
        dashboardId: Number.isFinite(dashboardId) ? dashboardId : null,
        acknowledgeLimitations: acknowledged,
        draft: draft.draft
      });
      if (!response?.data?.dashboardId) {
        setSaveError("The dashboard was not saved. The draft is still here.");
        return;
      }
      setSaved(response.data);
    } catch (err) {
      setSaveError(messageForFailure(err));
    } finally {
      setSaving(false);
    }
  };

  const ask = async (event) => {
    event.preventDefault();
    const message = question.trim();
    if (!message || loading || saving) return;
    setLoading(true);
    setError("");
    setAnswer("");
    setDraft(null);
    try {
      const response = await API.post("/api/ai/action", { message });
      const data = response?.data;
        if (data?.type === "ACTION_ERROR") {
          setError(data.message || data.error || "Commray AI returned an invalid structured response.");
          return;
        }
        if (data?.type === "ACTION" && data.draft && typeof data.draft === "object") {
          const requestId = typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `draft-${Date.now()}`;
          setSaved(null);
          setSaveError("");
          setAcknowledged(false);
          setDashboardChoice("");
          setDraft({ ...data, requestId });
          return;
        }
      const text = data?.answer;
      if (typeof text !== "string" || !text.trim()) {
        setError("Commray AI returned an empty response. Try again.");
        return;
      }
      setAnswer(text);
    } catch (err) {
      setError(messageForFailure(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button type="button" className="ask-ai-launch" onClick={() => setOpen(true)}>
        <Sparkles size={16} aria-hidden="true" />
        Ask Commray AI
      </button>
      {open && createPortal(
        <div className="ask-ai-backdrop" onMouseDown={close}>
          <div
            className="ask-ai-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="ask-ai-dialog-header">
              <h2 id={titleId}>Ask Commray AI</h2>
              <button type="button" className="ask-ai-close" onClick={close} aria-label="Close">
                ×
              </button>
            </div>
            <div className="ask-ai-scroll">
              <form onSubmit={ask}>
                <label className="ask-ai-label" htmlFor={`${titleId}-question`}>
                  Your question
                </label>
                <textarea
                  id={`${titleId}-question`}
                  className="ask-ai-input"
                  rows={4}
                  value={question}
                  placeholder="What is an HTTP 500 error?"
                  onChange={(event) => setQuestion(event.target.value)}
                  disabled={loading}
                />
                <div className="ask-ai-form-actions">
                  <button type="submit" className="ask-ai-submit" disabled={loading || saving || !question.trim()}>
                    {loading ? "Asking…" : "Ask AI"}
                  </button>
                  <button
                    type="button"
                    className="ask-ai-secondary"
                    onClick={clear}
                    disabled={loading || !hasContent}
                  >
                    Clear
                  </button>
                </div>
              </form>
              {error && (
                <p className="ask-ai-error" role="alert">
                  {error}
                </p>
              )}
              {answer && (
                <div className="ask-ai-answer" role="status">
                  <AiMarkdown text={answer} />
                </div>
              )}
              {draft && (
                <DraftView
                  onUseInExplorer={onUseInExplorer}
                  onConfirm={confirmDraft}
                  saving={saving}
                  saveError={saveError}
                  saved={saved}
                  dashboards={dashboards}
                  dashboardsLoaded={dashboardsLoaded}
                  dashboardChoice={dashboardChoice}
                  onDashboardChoice={setDashboardChoice}
                  acknowledged={acknowledged}
                  onAcknowledge={setAcknowledged}
                  result={{
                    ...draft,
                    onCancel: () => {
                      if (!saving) setDraft(null);
                    },
                    onEdit: () => {
                      if (!saving) setDraft(null);
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
